import { useEffect, useMemo, useRef, useState } from "react";
import type { AdminOpenClaw } from "../types/openclaw";
import type { ChannelStatus } from "../types/channel";
import { MESSAGE_CHANNELS, type MessageChannelDef } from "../config/messageChannels";
import { Logo } from "./dashboard/Logo";
import { useAdminChannels } from "../hooks/useAdminChannels";
import styles from "./AdminChannelsModal.module.css";

type Props = {
  open: boolean;
  claw: AdminOpenClaw | null;
  onClose: () => void;
  onFlash: (message: string) => void;
};

// "" = no row for this channel (not set). The rest mirror the persisted status
// values; picking "Not set" removes the channel row entirely.
const STATUS_OPTIONS: ReadonlyArray<{ value: ChannelStatus | ""; label: string }> = [
  { value: "", label: "Not set" },
  { value: "requested", label: "Requested" },
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "disconnected", label: "Disconnected" },
];

type RowFlash = "saved" | "error" | null;

export const AdminChannelsModal = ({ open, claw, onClose, onFlash }: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const tenantId = open && claw ? claw.tenantId : null;
  const { status, channels, error, setStatus, remove } = useAdminChannels(tenantId);
  const [rowFlash, setRowFlash] = useState<Record<string, RowFlash>>({});
  const [labelDrafts, setLabelDrafts] = useState<Record<string, string>>({});

  const stateBySlug = useMemo(
    () => new Map(channels.map((c) => [c.channel, c])),
    [channels],
  );

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setRowFlash({});
      setLabelDrafts({});
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  const flashRow = (slug: string, kind: RowFlash): void => {
    setRowFlash((prev) => ({ ...prev, [slug]: kind }));
    if (kind !== null) {
      window.setTimeout(() => {
        setRowFlash((prev) => {
          if (prev[slug] !== kind) return prev;
          const { [slug]: _ignored, ...rest } = prev;
          void _ignored;
          return rest;
        });
      }, 1800);
    }
  };

  const applyStatus = async (
    slug: string,
    next: ChannelStatus,
    accountLabel?: string | null,
  ): Promise<void> => {
    const result = await setStatus(slug, next, accountLabel);
    if (result.ok) {
      flashRow(slug, "saved");
    } else {
      flashRow(slug, "error");
      onFlash(`Update failed: ${result.message}`);
    }
  };

  const applyRemove = async (slug: string): Promise<void> => {
    const result = await remove(slug);
    if (result.ok) {
      flashRow(slug, "saved");
    } else {
      flashRow(slug, "error");
      onFlash(`Remove failed: ${result.message}`);
    }
  };

  const commitLabel = (slug: string, current: ChannelStatus | ""): void => {
    const draft = labelDrafts[slug];
    setLabelDrafts((prev) => {
      const { [slug]: _drop, ...rest } = prev;
      void _drop;
      return rest;
    });
    if (draft === undefined || current === "") return;
    const serverLabel = stateBySlug.get(slug)?.accountLabel ?? "";
    if (draft.trim() === serverLabel) return;
    void applyStatus(slug, current, draft.trim());
  };

  const renderRow = (def: MessageChannelDef): JSX.Element => {
    const state = stateBySlug.get(def.slug) ?? null;
    const current: ChannelStatus | "" = state?.status ?? "";
    const flash = rowFlash[def.slug] ?? null;
    const labelValue = labelDrafts[def.slug] ?? state?.accountLabel ?? "";
    return (
      <tr key={def.slug}>
        <td>
          <div className={styles.channelCell}>
            <Logo slug={def.slug} size={28} variant="brand" />
            <div className={styles.titleCell}>
              <span className={styles.slug}>{def.name}</span>
              <span className={styles.category}>{def.slug}</span>
            </div>
          </div>
        </td>
        <td>
          <select
            className={styles.select}
            value={current}
            aria-label={`Status for ${def.name}`}
            onChange={(e) => {
              const v = e.target.value as ChannelStatus | "";
              if (v === "") {
                void applyRemove(def.slug);
              } else {
                void applyStatus(def.slug, v);
              }
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {flash === "saved" && <span className={styles.savedFlash}>saved</span>}
          {flash === "error" && <span className={styles.errorFlash}>error</span>}
        </td>
        <td>
          <input
            type="text"
            className={styles.labelInput}
            value={labelValue}
            placeholder={current === "" ? "" : "e.g. @bot or +61..."}
            disabled={current === ""}
            aria-label={`Account label for ${def.name}`}
            onChange={(e) =>
              setLabelDrafts((prev) => ({ ...prev, [def.slug]: e.target.value }))
            }
            onBlur={() => commitLabel(def.slug, current)}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
        </td>
      </tr>
    );
  };

  if (!claw && !open) return null;

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <div className={styles.content}>
        <header className={styles.header}>
          <h2 className={styles.title}>Message channels</h2>
          {claw && (
            <p className={styles.subtitle}>
              <span className={styles.nameToken}>{claw.name}</span>
              {" - "}
              owned by {claw.ownerEmail}. Set how each channel reads for this tenant. Changes save inline.
            </p>
          )}
        </header>

        {error !== null && <div className={styles.errorBanner}>{error}</div>}

        {status === "loading" && <div className={styles.empty}>Loading channels...</div>}

        {status !== "loading" && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Channel</th>
                <th>Status</th>
                <th>Account label</th>
              </tr>
            </thead>
            <tbody>{MESSAGE_CHANNELS.map(renderRow)}</tbody>
          </table>
        )}

        <div className={styles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
};
